import horizontalScrollCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/HorizontalScrollCacheStateManager'
import { debounce } from '@src/quince-site-common/src/utils/debounce'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { ReactNode, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'

import { calculateSpaceBetweenSlides } from './snap-scroll-utils'
import {
  SNAP_SCROLL_DEBOUNCE_TIME,
  SNAP_SCROLL_INDICATOR_SYMBOL,
  SNAP_SCROLL_IS_HORIZONTAL,
  SnapScrollDirection,
  SnapScrollSnapType,
} from './snap-scroll.constant'
import styles from './snap-scroll.module.scss'
import { ISnapScrollProps, SnapScrollHandle } from './snap-scroll.types'

const SnapScroll = forwardRef<SnapScrollHandle, ISnapScrollProps>(
  (
    {
      isHorizontal = SNAP_SCROLL_IS_HORIZONTAL,
      direction = SnapScrollDirection.LTR,
      snapType = SnapScrollSnapType.Mandatory,
      children,
      containerClassName,
      slideClassName,
      spaceBetween,
      handleOnScrollEvent,
      scrollEventDebounceTime = SNAP_SCROLL_DEBOUNCE_TIME,
      isIndicatorAvailable,
      indicatorSymbol = SNAP_SCROLL_INDICATOR_SYMBOL,
      indicatorClassName,
      indicatorActiveClassName,
      id,
    },
    ref,
  ) => {
    const { t } = useTranslation('common')
    const containerRef = useRef<HTMLDivElement | null>(null)
    const lastActiveIndexRef = useRef<number | null>(0)
    const activeIndexRef = useRef<number | null>(0)
    const scrollLeftRef = useRef<number | null>(0)

    const handleScroll = () => {
      const currentIndex = getActiveIndex()
      if (currentIndex !== null && currentIndex !== lastActiveIndexRef.current) {
        lastActiveIndexRef.current = currentIndex
        if (handleOnScrollEvent) handleOnScrollEvent()
      }
    }
    useEffect(() => {
      const container = containerRef.current
      const debouncedHandleScroll = debounce(handleScroll, scrollEventDebounceTime)
      if (container) {
        container.addEventListener('scroll', debouncedHandleScroll)
        return () => {
          container.removeEventListener('scroll', debouncedHandleScroll)
        }
      }
    }, [handleOnScrollEvent, scrollEventDebounceTime])
    useEffect(() => {
      if (isHorizontal) {
        const horizontalScrollPosition = horizontalScrollCacheStateManager.get<number>(id)
        if (containerRef.current && horizontalScrollPosition) {
          containerRef.current.scrollLeft = horizontalScrollPosition
        }
      }
      return () => {
        if (isHorizontal) {
          horizontalScrollCacheStateManager.set(id, scrollLeftRef.current)
        }
      }
    }, [])
    const getActiveIndex = (): number | null => {
      const container = containerRef.current

      if (container) {
        const scrollPosition = isHorizontal ? container.scrollLeft : container.scrollTop
        const slideSize = isHorizontal ? container.offsetWidth : container.offsetHeight
        activeIndexRef.current = Math.round(scrollPosition / slideSize)
        if (isHorizontal) {
          scrollLeftRef.current = scrollPosition
        }
        return activeIndexRef.current
      }
      return null
    }
    useImperativeHandle(ref, () => ({
      getActiveIndex,
    }))
    const containerClass = classNames(containerClassName, styles.snapScrollContainer, styles[direction], {
      [styles.horizontal]: isHorizontal,
      [styles.vertical]: !isHorizontal,
      [styles[`${isHorizontal ? 'horizontal' : 'vertical'}-${snapType}`]]: snapType,
    })
    return (
      <div className={styles.snapScrollWrapper}>
        <div
          ref={containerRef}
          className={containerClass}
          role="region"
          aria-label={t('snap_scroll_container')}
          tabIndex={0}
          style={{ gap: calculateSpaceBetweenSlides(spaceBetween) }}
        >
          {React.Children.map(children, (child, index) => (
            <SnapScrollSlide key={index} index={index} slideClassName={slideClassName}>
              {child}
            </SnapScrollSlide>
          ))}
        </div>
        {/** These are visual indicators and not buttons */}
        {isIndicatorAvailable && (
          <div className={styles.indicators}>
            {React.Children.map(children, (_, index) => (
              <span
                key={index}
                className={classNames(indicatorClassName, {
                  [indicatorActiveClassName || '']: index === activeIndexRef.current,
                })}
              >
                {indicatorSymbol}
              </span>
            ))}
          </div>
        )}
      </div>
    )
  },
)

SnapScroll.displayName = 'SnapScroll'

// Individual slide component
const SnapScrollSlide: React.FC<{ index: number; slideClassName?: string; children: ReactNode }> = ({
  index,
  slideClassName,
  children,
}) => {
  const { t } = useTranslation('common')
  return (
    <div
      className={classNames(styles.snapScrollSlide, slideClassName)}
      aria-label={t('slide_var1', { var1: index + 1 })}
    >
      {children}
    </div>
  )
}
export { SnapScroll }
